import axios from 'axios';
import { FB_URL } from '../store/actions/types';
import { checkOpenAIStatus } from './lead_validation';

/**
 * Generates an AI-powered test/quiz based on lead content
 * @param {Object} leadContent - The lead content to base the test on
 * @param {Object} testConfig - Configuration for the test generation
 * @returns {Promise<Object>} - Generated test data with sections and questions
 */
export const generateAITest = async (leadContent, testConfig) => {
  try {
    // Check if OpenAI is available
    const openAIStatus = await checkOpenAIStatus();
    
    if (!openAIStatus.working) {
      throw new Error('AI is currently busy. Please use manual quiz creation instead.');
    }

    // Validate and sanitize configuration
    const sanitizedConfig = sanitizeTestConfig(testConfig);
    
    
    // Generate the test using OpenAI
    const generatedTest = await callOpenAIForTestGeneration(leadContent, sanitizedConfig);
    
    
    // Validate and format the response
    let formattedTest = formatTestResponse(generatedTest, leadContent, sanitizedConfig);
    

    // If fewer questions than requested, try one automatic continuation request
    const requested = sanitizedConfig.totalQuestions;
    const actual = formattedTest?.metadata?.totalQuestions || 0;
    if (actual < requested) {
      const remaining = requested - actual;
      
      const existingQuestions = formattedTest.questions?.map(q => q.question) || [];
      const additionalRaw = await callOpenAIForAdditionalQuestions(leadContent, { ...sanitizedConfig, totalQuestions: remaining }, existingQuestions);
      
      const additionalQuestions = parseSimpleTextFormat(additionalRaw);
      const combinedQuestions = [...(formattedTest.questions || []), ...additionalQuestions].slice(0, requested);
      const combinedSections = createSectionsFromQuestions(combinedQuestions, sanitizedConfig);
      formattedTest = {
        sections: combinedSections,
        questions: combinedQuestions,
        metadata: {
          ...formattedTest.metadata,
          totalQuestions: combinedQuestions.length,
          requestedQuestions: requested,
          hasFewerQuestions: combinedQuestions.length < requested,
          questionsShortfall: Math.max(0, requested - combinedQuestions.length)
        }
      };
      
    }

    return formattedTest;
    
  } catch (error) {
    
    throw error;
  }
};

/**
 * Regenerates a single MCQ based on the same lead content and difficulty
 * while avoiding duplicates and the specific question being replaced.
 * @param {Object} leadContent - The lead content to base the question on
 * @param {Object} config - { difficulty?: 'basic'|'medium'|'hard' }
 * @param {Array<string>} existingQuestions - List of existing question stems to avoid
 * @param {string} avoidQuestionText - The exact question text to avoid rephrasing
 * @returns {Promise<{question: string, options: string[], correctAnswer: string}>}
 */
export const regenerateSingleQuestion = async (
  leadContent,
  config = {},
  existingQuestions = [],
  avoidQuestionText = ''
) => {
  // Ensure AI is available first
  const openAIStatus = await checkOpenAIStatus();
  if (!openAIStatus.working) {
    throw new Error('AI is currently busy. Please try again shortly.');
  }

  const difficulty = config.difficulty || 'basic';

  // Prioritize transcript over other content
  const isTranscriptAvailable = leadContent.transcript && leadContent.transcript.trim() !== '';
  const primaryContent = isTranscriptAvailable ? leadContent.transcript : (leadContent.content || leadContent.title);
  const contentLabel = isTranscriptAvailable ? 'Transcript' : 'Content';

  // Limit how many existing questions we send to the model
  const existingList = (existingQuestions || [])
    .filter(Boolean)
    .slice(0, 25)
    .map((q, i) => `${i + 1}. ${q}`)
    .join('\n');

  const avoidLine = avoidQuestionText && avoidQuestionText.trim() !== ''
    ? `- Do NOT repeat, rephrase, or be overly similar to this question being replaced: \n"${avoidQuestionText.trim()}"\n`
    : '';

  const prompt = `
Based on the following ${contentLabel.toLowerCase()}, create 1 high-quality multiple choice question for a ${difficulty} level quiz.

${contentLabel}: "${primaryContent}"

REQUIREMENTS:
- Create exactly 1 question
- Provide exactly 4 options (A, B, C, D)
- Only one option should be correct
- Randomize the correct option position across A-D
- Keep the question clear, concise, and relevant
- Avoid duplicates or near-duplicates of existing questions
${avoidLine}- Avoid using ambiguous phrasing or options

EXISTING QUESTIONS TO AVOID (for context):
${existingList || '(none)'}

RESPONSE FORMAT (no extra text):
Question: [Your question here]
A) [Option A]
B) [Option B]
C) [Option C]
D) [Option D]
Correct: [A, B, C, or D]
`;

  // Call the same gateway
  const timeoutDuration = 45000; // 45s is enough for a single question
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutDuration);

  try {
    const response = await axios.post(`${FB_URL}/m103-chatgpt`, {
      question: prompt,
      email: 'ai_test_single_question@thinklead.app'
    }, {
      signal: controller.signal,
      timeout: timeoutDuration,
      headers: { 'Content-Type': 'application/json' }
    });

    clearTimeout(timeoutId);

    const aiResponse = response.data.answer || response.data.data;
    if (!aiResponse) {
      throw new Error('No response received from AI.');
    }

    const parsed = parseSimpleTextFormat(aiResponse);
    if (!parsed || parsed.length === 0) {
      throw new Error('AI returned an invalid question format.');
    }
    // Use the first valid question
    const q = parsed[0];
    return {
      question: q.question,
      options: q.options,
      correctAnswer: q.correctAnswer
    };
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('AI is taking too long to respond. Please try again.');
    }
    if (error.response?.status === 429) {
      throw new Error('AI service is rate limited. Please retry in a moment.');
    }
    if (error.response?.status >= 500) {
      throw new Error('AI service is temporarily down. Please try later.');
    }
    throw new Error(error.message || 'Failed to regenerate question.');
  }
};

/**
 * Sanitizes and validates test configuration
 * @param {Object} config - Test configuration
 * @returns {Object} - Sanitized configuration
 */
const sanitizeTestConfig = (config) => {
  // Allow larger transcripts/tests: keep same per-section caps for UI UX, but do not downscale totalQuestions below requested
  const numberOfSections = Math.min(Math.max(config.numberOfSections || 2, 1), 3);
  const questionsPerSection = Math.min(Math.max(config.questionsPerSection || 5, 1), 5);
  const requestedTotal = (config.numberOfSections || 2) * (config.questionsPerSection || 5);
  return {
    numberOfSections,
    questionsPerSection,
    difficulty: config.difficulty || 'basic',
    // Preserve requested total up to 15 by default; if caller asks more later, we can raise this cap
    totalQuestions: Math.min(requestedTotal, 15)
  };
};

/**
 * Calls OpenAI API to generate test content
 * @param {Object} leadContent - Lead content for context
 * @param {Object} config - Sanitized test configuration
 * @returns {Promise<Object>} - Raw OpenAI response
 */
const callOpenAIForTestGeneration = async (leadContent, config) => {
  const prompt = createTestGenerationPrompt(leadContent, config);
  
  
  try {
    // Set timeout for the request (longer to allow full 15 Q generation)
    const timeoutDuration = 90000; // 90 seconds for test generation
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeoutDuration);

    const requestPayload = {
      question: prompt,
      email: 'ai_test_generation@thinklead.app'
    };

    console.log('AI test payload here: ', requestPayload);
    
    const response = await axios.post(`${FB_URL}/m103-chatgpt`, requestPayload, { 
      signal: controller.signal,
      timeout: timeoutDuration,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Clear the timeout if successful
    clearTimeout(timeoutId);

    // Get the AI response
    
    const aiResponse = response.data.answer || response.data.data;
    
    if (!aiResponse) {
      throw new Error('No response received from OpenAI API');
    }

    return aiResponse;
    
  } catch (error) {
    
    
    
    
    
    
    
    if (error.name === 'AbortError') {
      throw new Error('AI is taking too long to respond. Please use manual quiz creation instead.');
    }
    
    if (error.response?.status === 429) {
      throw new Error('AI service is rate limited. Please try again in a few minutes.');
    }
    
    if (error.response?.status >= 500) {
      throw new Error('AI service is temporarily down. Please use manual quiz creation instead.');
    }
    
    throw new Error(`AI request failed: ${error.message || 'Unknown error'}`);
  }
};

/**
 * Requests additional questions to reach the target count
 * @param {Object} leadContent
 * @param {Object} config - includes totalQuestions as remaining count
 * @param {Array<string>} existingQuestions - stems to avoid repeating
 */
const callOpenAIForAdditionalQuestions = async (leadContent, config, existingQuestions) => {
  const prompt = createAdditionalQuestionsPrompt(leadContent, config, existingQuestions);
  
  try {
    const timeoutDuration = 90000;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutDuration);
    const requestPayload = { question: prompt, email: 'ai_test_generation@thinklead.app' };
    
    const response = await axios.post(`${FB_URL}/m103-chatgpt`, requestPayload, {
      signal: controller.signal,
      timeout: timeoutDuration,
      headers: { 'Content-Type': 'application/json' }
    });
    clearTimeout(timeoutId);
    
    const aiResponse = response.data.answer || response.data.data;
    if (!aiResponse) {
      throw new Error('No response received from OpenAI API (additional)');
    }
    return aiResponse;
  } catch (error) {
    
    throw error;
  }
};

/**
 * Creates a comprehensive prompt for OpenAI test generation
 * @param {Object} leadContent - Lead content for context
 * @param {Object} config - Test configuration
 * @returns {string} - Formatted prompt for OpenAI
 */
const createTestGenerationPrompt = (leadContent, config) => {
  // DEBUG: Log what we receive for test generation
  
  
  
  

  // Prioritize transcript over other content
  const isTranscriptAvailable = leadContent.transcript && leadContent.transcript.trim() !== '';
  const primaryContent = isTranscriptAvailable ? leadContent.transcript : leadContent.content || leadContent.title;
  const contentLabel = isTranscriptAvailable ? 'Transcript' : 'Content';
  
  // DEBUG: Log what we're using for generation
  
  
  
  
  const prompt = `
Based on the following content, create ${config.totalQuestions} multiple choice questions for a ${config.difficulty} level quiz.

CONTENT TO BASE QUESTIONS ON:
${contentLabel}: "${primaryContent}"

REQUIREMENTS:
- Create exactly ${config.totalQuestions} questions
- Each question should have exactly 4 options (A, B, C, D)
- Only one option should be correct according to the content provided
- Make all answer choices ethically reasonable and professionally worded; the correct option must be accurate strictly according to the source content, while distractors may also be ethically sound but must be factually or contextually incorrect. 
- Randomize the position of the correct option across A, B, C, D
- Aim to evenly distribute correct option positions across A, B, C, and D
- Questions should be relevant to the content provided
- Keep questions clear and professional
- All options are similar in length and structure
- No option stands out due to extra detail or wording
- Avoid extreme words like "always" or "never" unless essential
- The distractors should require real understanding to eliminate
- For each question, base distractors on, common logical errors, overgeneralizations, misapplied definitions and confusion between closely related terms

RESPONSE FORMAT - Provide your response in this simple format:

Question: [Your question here]
A) [Option A]
B) [Option B] 
C) [Option C]
D) [Option D]
Correct: [A, B, C, or D]

Question: [Your next question here]
A) [Option A]
B) [Option B]
C) [Option C] 
D) [Option D]
Correct: [A, B, C, or D]

Continue this pattern for all ${config.totalQuestions} questions. Do not include any other text, explanations, or formatting.`;

  // DEBUG: Log the full prompt being sent to AI
  
  
  return prompt;
};

/**
 * Creates a continuation prompt to generate remaining questions
 */
const createAdditionalQuestionsPrompt = (leadContent, config, existingQuestions) => {
  const base = createTestGenerationPrompt(leadContent, config);
  const existingList = (existingQuestions || []).slice(0, 20).map((q, i) => `${i + 1}. ${q}`).join('\n');
  const appendix = `\n\nIMPORTANT:\n- Generate exactly ${config.totalQuestions} NEW questions that do not repeat or rephrase the following existing questions:\n${existingList || '(no prior questions provided)'}\n- Do not include any text other than the questions in the specified format.`;
  return base + appendix;
};

/**
 * Formats and validates the OpenAI response
 * @param {string} aiResponse - Raw response from OpenAI
 * @param {Object} leadContent - Lead content for context
 * @param {Object} config - Test configuration
 * @returns {Object} - Formatted test data
 */
const formatTestResponse = (aiResponse, leadContent, config) => {
  try {
    // Parse the simple text format from AI
    const questions = parseSimpleTextFormat(aiResponse);
    
    if (questions.length === 0) {
      throw new Error('No valid questions found in AI response');
    }
    
    // Rebalance the correct option positions across A-D for fairness
    const balancedQuestions = rebalanceCorrectOptionPositions(questions);
    
    // Check if AI generated fewer questions than requested
    const requestedQuestions = config.totalQuestions;
    const actualQuestions = balancedQuestions.length;
    const hasFewerQuestions = actualQuestions < requestedQuestions;
    
    // Create sections on our end
    const sections = createSectionsFromQuestions(balancedQuestions, config);
    
    return {
      sections: sections,
      // Expose parsed questions to allow continuation/merging if needed
      questions: balancedQuestions,
      metadata: {
        totalQuestions: balancedQuestions.length,
        requestedQuestions: requestedQuestions,
        hasFewerQuestions: hasFewerQuestions,
        questionsShortfall: hasFewerQuestions ? requestedQuestions - actualQuestions : 0,
        difficulty: config.difficulty,
        category: leadContent.category || 'Generated Quiz',
        generatedAt: new Date().toISOString()
      }
    };

  } catch (error) {
    throw new Error('AI test generation failed. Please use manual quiz creation instead.');
  }
};

/**
 * Parses the simple text format from AI response
 * @param {string} aiResponse - Raw AI response
 * @returns {Array} - Array of parsed questions
 */
const parseSimpleTextFormat = (aiResponse) => {
  const questions = [];

  // Split response into question blocks
  const questionBlocks = aiResponse.split(/Question:/i).filter(block => block.trim());

  questionBlocks.forEach((block) => {
    try {
      const lines = block
        .trim()
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line);

      if (lines.length < 6) {
        return;
      }

      const questionText = lines[0];
      const options = [];
      let correctIndex = -1;

      // Accept A) / A. / A: formats and ignore empty option texts
      const optionRegex = /^[A-D][)\.:]\s*(.+)$/i;
      const correctRegex = /^Correct(?:\s*Answer)?\s*:\s*([A-D])/i;

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i];

        const optMatch = line.match(optionRegex);
        if (optMatch) {
          const optionText = (optMatch[1] || '').trim();
          if (optionText) {
            options.push(optionText);
          } else {
            // push a placeholder to keep indexing but we'll invalidate later
            options.push('');
          }
          continue;
        }

        const corrMatch = line.match(correctRegex);
        if (corrMatch) {
          const letter = corrMatch[1].toUpperCase();
          correctIndex = letter.charCodeAt(0) - 65; // A=0..D=3
        }
      }

      // Validate question: all four non-empty, unique, and valid correct index
      if (!questionText || options.length !== 4) {
        return;
      }

      const trimmedOptions = options.map((o) => (o || '').trim());
      if (trimmedOptions.some((o) => !o)) {
        return;
      }
      const uniqueCount = new Set(trimmedOptions.map((o) => o.toLowerCase())).size;
      if (uniqueCount !== 4) {
        return;
      }
      if (correctIndex < 0 || correctIndex >= trimmedOptions.length) {
        return;
      }

      const correctAnswer = trimmedOptions[correctIndex];

      questions.push({
        id: `q_${questions.length + 1}`,
        question: questionText,
        options: trimmedOptions,
        correctAnswer
      });
    } catch (_) {
      // Skip invalid blocks silently
    }
  });

  return questions;
};

/**
 * Shuffles array in place using Fisher-Yates algorithm
 * @param {Array} array
 * @returns {Array}
 */
const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

/**
 * Rebalances correct option positions across A-D for fairness.
 * Keeps `correctAnswer` as the authoritative answer (string match),
 * and rearranges `options` so the correct answer appears at a balanced index.
 * @param {Array<{options: string[], correctAnswer: string}>} questions
 * @returns {Array}
 */
const rebalanceCorrectOptionPositions = (questions) => {
  if (!Array.isArray(questions) || questions.length === 0) return questions;
  
  const total = questions.length;
  const fullCycles = Math.floor(total / 4);
  const remainder = total % 4;
  const targetPositions = [];
  for (let c = 0; c < fullCycles; c++) {
    targetPositions.push(0, 1, 2, 3);
  }
  // Distribute remaining positions starting from 0
  for (let r = 0; r < remainder; r++) {
    targetPositions.push(r);
  }
  // Shuffle target positions to avoid predictable placement patterns
  shuffleArray(targetPositions);
  
  questions.forEach((q, idx) => {
    if (!q || !Array.isArray(q.options) || q.options.length !== 4 || !q.correctAnswer) return;
    const currentOptions = [...q.options];
    const correctIdx = currentOptions.findIndex((opt) => opt === q.correctAnswer);
    if (correctIdx === -1) return;
    const targetPos = targetPositions[idx % targetPositions.length];
    
    const distractors = currentOptions.filter((_, i) => i !== correctIdx);
    shuffleArray(distractors);
    
    const rebuilt = [];
    let dIndex = 0;
    for (let i = 0; i < 4; i++) {
      if (i === targetPos) rebuilt.push(q.correctAnswer);
      else rebuilt.push(distractors[dIndex++]);
    }
    q.options = rebuilt;
  });
  
  return questions;
};

/**
 * Creates sections from questions array
 * @param {Array} questions - Array of questions
 * @param {Object} config - Test configuration
 * @returns {Array} - Array of sections
 */
const createSectionsFromQuestions = (questions, config) => {
  const sections = [];
  const numberOfSections = config.numberOfSections;
  const totalQuestions = questions.length;
  
  // Calculate how many questions should go in each section
  const baseQuestionsPerSection = Math.floor(totalQuestions / numberOfSections);
  const remainderQuestions = totalQuestions % numberOfSections;
  
  let currentIndex = 0;
  
  for (let i = 0; i < numberOfSections; i++) {
    // Some sections get one extra question if there's a remainder
    const questionsInThisSection = baseQuestionsPerSection + (i < remainderQuestions ? 1 : 0);
    
    const sectionQuestions = questions.slice(currentIndex, currentIndex + questionsInThisSection);
    
    if (sectionQuestions.length > 0) {
      sections.push({
        id: i + 1,
        name: `Section ${i + 1}`,
        questions: sectionQuestions
      });
    }
    
    currentIndex += questionsInThisSection;
  }
  
  return sections;
};

 